import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
  Package, Search, Eye, Edit, Receipt, Trash2, Check, X, Truck, 
  MapPin, Building2, Clock, CheckCircle, AlertCircle, RefreshCw, Download, ClipboardList, CalendarDays, UserCheck, Mail, Send, Link2, Camera, Upload, Printer, Settings
} from 'lucide-react';
import { PermissionGate, usePermissions } from '../../context/PermissionContext';
import { groupCartByRoom } from '../../lib/cartGroups';
import { openInstallSheet } from '../../lib/installSheet';
import { openPackingSlip } from '../../lib/packingSlip';
import { useAuth } from '../../context/AuthContext';
import { taxLabel, isFullAdmin } from '../tax/taxDisplay';
import { TaxControls, TaxReadOnly } from '../tax/TaxControls';
import { TaxExplainLine, TestBadge, NexusBanner } from '../tax/TaxBadges';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const FULFILLMENT_TYPES = [
  { id: 'shipping', name: 'Shipping', icon: Truck, description: 'Ship to customer address' },
  { id: 'pickup', name: 'Pickup', icon: MapPin, description: 'Customer picks up at our location' },
  { id: 'delivery', name: 'Local Delivery', icon: Building2, description: 'We deliver locally' }
];

const STATUS_COLORS = {
  draft: 'bg-neutral-100 text-neutral-700',
  pending_admin_approval: 'bg-amber-100 text-amber-700',
  pending_customer_approval: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-green-100 text-green-700',
  changes_requested: 'bg-red-100 text-red-700',
  converted_to_invoice: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-neutral-100 text-neutral-700',
  // Legacy
  pending_approval: 'bg-amber-100 text-amber-700'
};

const STATUS_LABELS = {
  draft: 'Draft',
  pending_admin_approval: 'Awaiting Admin Approval',
  pending_customer_approval: 'Awaiting Customer Approval',
  approved: 'Fully Approved',
  changes_requested: 'Changes Requested',
  converted_to_invoice: 'Invoiced',
  cancelled: 'Cancelled',
  // Legacy
  pending_approval: 'Pending Approval'
};

export const SalesOrdersTab = ({ token }) => {
  const { user: authUser } = useAuth();
  const [salesOrders, setSalesOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedSO, setSelectedSO] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({
    fulfillment_type: 'shipping',
    pickup_location: '',
    delivery_notes: '',
    notes: '',
    installation_date: '',
    assigned_installer_id: '',
    assigned_installer_name: ''
  });
  const { isAdmin } = usePermissions();
  const [installers, setInstallers] = useState([]);
  const [showLabelSettings, setShowLabelSettings] = useState(false);
  const [labelFields, setLabelFields] = useState({
    room_window: true, product_type: true, size: true, fabric: true, mount_type: true, shipping_address: true
  });
  const [savingLabels, setSavingLabels] = useState(false);

  const fetchSalesOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/sales-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSalesOrders(response.data);
    } catch (error) {
      toast.error('Failed to fetch sales orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchInstallers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/installers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInstallers(response.data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchSalesOrders();
    fetchInstallers();
    // Load label settings
    axios.get(`${API_URL}/admin/label-settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if (res.data?.fields) setLabelFields(res.data.fields); })
      .catch(() => {});
  }, [token]);

  const saveLabelSettings = async () => {
    setSavingLabels(true);
    try {
      await axios.put(`${API_URL}/admin/label-settings`, { fields: labelFields }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Label settings saved');
      setShowLabelSettings(false);
    } catch { toast.error('Failed to save settings'); }
    finally { setSavingLabels(false); }
  };

  const convertToInvoice = async (soId) => {
    if (!window.confirm('Convert this Sales Order to an Invoice?')) return;
    
    try {
      const response = await axios.post(`${API_URL}/admin/sales-orders/${soId}/convert-to-invoice`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Invoice ${response.data.invoice_number} created!`);
      fetchSalesOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert to invoice');
    }
  };

  const emailDocument = async (soId, docType, customerEmail) => {
    if (!customerEmail) {
      toast.error('No customer email on this order');
      return;
    }
    try {
      await axios.post(`${API_URL}/admin/sales-orders/${soId}/email-document`, 
        { doc_type: docType, to_email: customerEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${docType === 'packing_slip' ? 'Packing Slip' : 'Install Sheet'} sent to ${customerEmail}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    }
  };


  const approveSO = async (soId) => {
    try {
      const response = await axios.put(`${API_URL}/sales-orders/${soId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.fully_approved) {
        if (response.data.invoice_number) {
          toast.success(`Sales Order fully approved! Invoice ${response.data.invoice_number} generated.`);
        } else {
          toast.success('Sales Order fully approved!');
        }
      } else if (response.data.admin_approved && !response.data.customer_approved) {
        toast.success('Admin approval recorded. Waiting for customer approval.');
      } else if (response.data.customer_approved && !response.data.admin_approved) {
        toast.success('Customer approval recorded. Waiting for admin approval.');
      } else {
        toast.success('Approval recorded');
      }
      
      fetchSalesOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const updateSO = async () => {
    try {
      await axios.put(`${API_URL}/admin/sales-orders/${selectedSO.id}/update`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sales Order updated');
      setShowEditModal(false);
      fetchSalesOrders();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const deleteSO = async (soId, soNumber) => {
    if (!window.confirm(`Are you sure you want to delete Sales Order ${soNumber}? This action cannot be undone.`)) return;
    
    try {
      await axios.delete(`${API_URL}/admin/sales-orders/${soId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Sales Order ${soNumber} deleted`);
      fetchSalesOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete Sales Order');
    }
  };

  const handleDownloadPDF = async (soId, soNumber) => {
    try {
      const response = await axios.get(`${API_URL}/sales-orders/${soId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SalesOrder_${soNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleBatchLabels = async (soId, soNumber) => {
    try {
      const response = await axios.get(`${API_URL}/admin/sales-orders/${soId}/batch-labels`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      toast.success(`Labels for SO #${soNumber} opened for printing`);
    } catch (error) {
      toast.error('Failed to generate labels');
    }
  };

  const filteredOrders = salesOrders.filter(so => {
    const matchesSearch = 
      String(so.so_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      so.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      so.customer_email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || so.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price || 0);
  };

  if (loading) {
    return <div className="text-center py-12">Loading sales orders...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Sales Orders</h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowLabelSettings(true)} title="Label Settings" data-testid="label-settings-btn">
            <Settings className="w-4 h-4 mr-2" /> Label Settings
          </Button>
          <Button variant="outline" size="sm" onClick={fetchSalesOrders}>
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input
            placeholder="Search by SO number, customer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-neutral-200 rounded-lg"
        >
          <option value="all">All Status</option>
          <option value="pending_approval">Pending Approval</option>
          <option value="approved">Approved</option>
          <option value="changes_requested">Changes Requested</option>
          <option value="converted_to_invoice">Invoiced</option>
        </select>
      </div>

      {/* Sales Orders List */}
      {filteredOrders.length === 0 ? (
        <div className="text-center py-12 bg-neutral-50 rounded-lg">
          <Package className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
          <p className="text-neutral-500">No sales orders found</p>
        </div>
      ) : (
        <div className="border border-neutral-200 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-neutral-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">SO #</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Customer</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Fulfillment</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Total</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Status</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-neutral-600">Date</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-neutral-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map(so => (
                <tr key={so.id} className="border-b last:border-0 hover:bg-neutral-50">
                  <td className="px-4 py-3 font-mono text-sm">{so.so_number}<div className="mt-1"><TestBadge doc={so} testId={`so-test-badge-${so.so_number}`} /></div>{(so.nexus_review_state || so.tax_details?.nexus_review) && <div className="mt-1 text-[11px] text-red-700 font-sans" data-testid={`so-nexus-flag-${so.so_number}`}>Nexus review needed</div>}</td>
                  <td className="px-4 py-3">
                    <p className="font-medium">{so.customer_name}</p>
                    <p className="text-sm text-neutral-500">{so.customer_email}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                      so.fulfillment_type === 'shipping' ? 'bg-blue-50 text-blue-700' :
                      so.fulfillment_type === 'pickup' ? 'bg-purple-50 text-purple-700' :
                      'bg-green-50 text-green-700'
                    }`}>
                      {so.fulfillment_type === 'shipping' && <Truck className="w-3 h-3" />}
                      {so.fulfillment_type === 'pickup' && <MapPin className="w-3 h-3" />}
                      {so.fulfillment_type === 'delivery' && <Building2 className="w-3 h-3" />}
                      {so.fulfillment_type?.charAt(0).toUpperCase() + so.fulfillment_type?.slice(1)}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-medium">{formatPrice(so.total)}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium inline-block w-fit ${STATUS_COLORS[so.status] || 'bg-neutral-100'}`}>
                        {STATUS_LABELS[so.status] || so.status}
                      </span>
                      {/* Dual approval status indicators */}
                      <div className="flex items-center gap-2 text-xs">
                        <span className={`flex items-center gap-0.5 ${so.admin_approved ? 'text-green-600' : 'text-neutral-400'}`}>
                          {so.admin_approved ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                          Admin
                        </span>
                        <span className={`flex items-center gap-0.5 ${so.customer_approved ? 'text-green-600' : 'text-neutral-400'}`}>
                          {so.customer_approved ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                          Customer
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-neutral-500">
                    {new Date(so.created_at).toLocaleDateString()}
                    {so.installation_date && (
                      <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-50 text-amber-700 text-xs font-medium" data-testid={`so-install-badge-${so.so_number}`}>
                        <CalendarDays className="w-3 h-3" />
                        Install {new Date(so.installation_date + 'T00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {/* Admin approve button - show if admin hasn't approved yet */}
                      {!so.admin_approved && so.status !== 'cancelled' && so.status !== 'converted_to_invoice' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => approveSO(so.id)}
                          className="text-green-600 border-green-300 hover:bg-green-50"
                          title="Admin Approve"
                          data-testid="admin-approve-so-btn"
                        >
                          <Check className="w-4 h-4 mr-1" /> Approve
                        </Button>
                      )}
                      {/* Legacy pending_approval check */}
                      {so.status === 'pending_approval' && so.admin_approved !== false && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => approveSO(so.id)}
                          className="text-green-600 border-green-300 hover:bg-green-50"
                          title="Approve"
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                      )}
                      {so.status === 'approved' && !so.invoice_id && (
                        <Button
                          size="sm"
                          onClick={() => convertToInvoice(so.id)}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          <Receipt className="w-4 h-4 mr-1" /> Invoice
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedSO(so);
                          setEditData({
                            fulfillment_type: so.fulfillment_type || 'shipping',
                            pickup_location: so.pickup_location || '',
                            delivery_notes: so.delivery_notes || '',
                            notes: so.notes || '',
                            installation_date: so.installation_date || '',
                            assigned_installer_id: so.assigned_installer_id || '',
                            assigned_installer_name: so.assigned_installer_name || ''
                          });
                          setShowEditModal(true);
                        }}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedSO(so)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadPDF(so.id, so.so_number)}
                        title="Download PDF"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openInstallSheet(so, 'Sales Order')}
                        title="Print Install Sheet"
                        data-testid={`so-install-sheet-${so.so_number}`}
                      >
                        <ClipboardList className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openPackingSlip(so, 'Sales Order')}
                        title="Print Packing Slip"
                        data-testid={`so-packing-slip-${so.so_number}`}
                      >
                        <Package className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => emailDocument(so.id, 'install_sheet', so.customer_email)}
                        title="Email Install Sheet to Customer"
                        data-testid={`so-email-install-${so.so_number}`}
                        className="text-amber-600 hover:text-amber-700 hover:border-amber-300"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                      {so.tracking_code && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const url = `${window.location.origin}/track/order/${so.tracking_code}`;
                            navigator.clipboard.writeText(url);
                            toast.success('Tracking link copied!');
                          }}
                          title="Copy Order Tracking Link"
                          data-testid={`so-copy-track-${so.so_number}`}
                          className="text-blue-600 hover:text-blue-700 hover:border-blue-300"
                        >
                          <Link2 className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleBatchLabels(so.id, so.so_number)}
                        title="Print Shipping Labels"
                        data-testid={`so-batch-labels-${so.so_number}`}
                        className="text-purple-600 hover:text-purple-700 hover:border-purple-300"
                      >
                        <Printer className="w-4 h-4" />
                      </Button>
                      {/* Delete button - show for users with full permissions, only when not invoiced (unless Super Admin) */}
                      <PermissionGate module="sales_orders" level="full">
                      {(isAdmin || !so.invoice_id) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteSO(so.id, so.so_number)}
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          title={so.invoice_id ? "Delete (has linked invoice)" : "Delete"}
                          data-testid={`delete-so-${so.so_number}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                      </PermissionGate>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* View SO Modal */}
      {selectedSO && !showEditModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedSO(null)}>
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-neutral-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Sales Order {selectedSO.so_number}</h2>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[selectedSO.status]}`}>
                  {STATUS_LABELS[selectedSO.status]}
                </span>
              </div>
              <button onClick={() => setSelectedSO(null)} className="p-2 hover:bg-neutral-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Customer Info */}
              <div>
                <h3 className="font-medium mb-2">Customer</h3>
                <p>{selectedSO.customer_name}</p>
                <p className="text-neutral-500">{selectedSO.customer_email}</p>
                {selectedSO.company_name && <p className="text-neutral-500">{selectedSO.company_name}</p>}
              </div>

              {/* Fulfillment Info */}
              <div>
                <h3 className="font-medium mb-2">Fulfillment</h3>
                <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg ${
                  selectedSO.fulfillment_type === 'shipping' ? 'bg-blue-50 text-blue-700' :
                  selectedSO.fulfillment_type === 'pickup' ? 'bg-purple-50 text-purple-700' :
                  'bg-green-50 text-green-700'
                }`}>
                  {selectedSO.fulfillment_type === 'shipping' && <Truck className="w-4 h-4" />}
                  {selectedSO.fulfillment_type === 'pickup' && <MapPin className="w-4 h-4" />}
                  {selectedSO.fulfillment_type === 'delivery' && <Building2 className="w-4 h-4" />}
                  <span className="font-medium">{selectedSO.fulfillment_type ? selectedSO.fulfillment_type.charAt(0).toUpperCase() + selectedSO.fulfillment_type.slice(1) : 'Not set'}</span>
                </div>
                {selectedSO.pickup_location && (
                  <p className="text-sm text-neutral-600 mt-2">Pickup at: {selectedSO.pickup_location}</p>
                )}
                {selectedSO.delivery_notes && (
                  <p className="text-sm text-neutral-600 mt-2">Notes: {selectedSO.delivery_notes}</p>
                )}
              </div>

              {/* Items */}
              <div>
                <h3 className="font-medium mb-2">Items</h3>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-neutral-50">
                      <tr>
                        <th className="text-left px-3 py-2">Item</th>
                        <th className="text-right px-3 py-2">Qty</th>
                        <th className="text-right px-3 py-2">Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupCartByRoom(selectedSO.items).map((group) => (
                        <React.Fragment key={group.room}>
                          <tr className="border-t bg-stone-50" data-testid={`so-room-group-${group.room.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                            <td className="px-3 py-2 font-semibold">{group.room} <span className="text-xs font-normal text-neutral-400">· {group.quantity} {group.quantity === 1 ? 'shade' : 'shades'}</span></td>
                            <td className="px-3 py-2 text-right text-neutral-400">{group.quantity}</td>
                            <td className="px-3 py-2 text-right font-semibold">{formatPrice(group.subtotal)}</td>
                          </tr>
                          {group.items.map((item, idx) => (
                            <tr key={item.id || `item-${idx}`} className="border-t">
                              <td className="px-3 py-2 pl-6">{item.windowLabel || item.config?.product_type}{item.windowLabel && <span className="text-neutral-400"> · {item.config?.product_type}</span>}<span className="text-neutral-400 text-xs"> · {item.config?.width}" × {item.config?.height}"</span></td>
                              <td className="px-3 py-2 text-right">{item.quantity}</td>
                              <td className="px-3 py-2 text-right">{formatPrice(item.config?.total_price)}</td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totals */}
              <div className="border-t pt-4">
                <div className="flex justify-between mb-1">
                  <span>Subtotal</span>
                  <span>{formatPrice(selectedSO.subtotal)}</span>
                </div>
                <div className="flex justify-between mb-1">
                  <span data-testid="so-tax-label">{taxLabel(selectedSO)}</span>
                  <span>{selectedSO.tax_details?.status === 'pending_address' ? 'TBC' : formatPrice(selectedSO.tax)}</span>
                </div>
                <div className="mb-2"><TaxExplainLine doc={selectedSO} testId="so-tax-explain" /></div>
                <NexusBanner doc={selectedSO} testId="so-nexus-banner" />
                <div className="mt-2"><TestBadge doc={selectedSO} testId="so-detail-test-badge" /></div>
                <div className="flex justify-between font-semibold text-lg">
                  <span>Total</span>
                  <span>{formatPrice(selectedSO.total)}</span>
                </div>
              </div>

              {!isFullAdmin(authUser) && <TaxReadOnly doc={selectedSO} />}
              {isFullAdmin(authUser) && selectedSO.payment_status !== 'paid' && (
                <TaxControls kind="sales-orders" doc={selectedSO} token={token}
                  onChange={(r) => { setSelectedSO((so) => ({ ...so, ...r })); fetchSalesOrders(); }} />
              )}

              {/* Install Scheduling */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-amber-50 px-4 py-2 border-b flex items-center gap-2">
                  <CalendarDays className="w-4 h-4 text-amber-600" />
                  <h3 className="font-medium text-sm text-amber-800">Installation Schedule</h3>
                </div>
                <div className="p-4 space-y-3">
                  {selectedSO.installation_date ? (
                    <div className="flex items-center gap-3">
                      <CalendarDays className="w-5 h-5 text-amber-500" />
                      <div>
                        <p className="font-medium">{new Date(selectedSO.installation_date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
                        {selectedSO.assigned_installer_name && (
                          <p className="text-sm text-neutral-500 flex items-center gap-1"><UserCheck className="w-3.5 h-3.5" /> {selectedSO.assigned_installer_name}</p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-neutral-400 italic">No installation date scheduled yet. Click Edit to assign one.</p>
                  )}
                </div>
              </div>

              {/* Delivery Photos */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-green-50 px-4 py-2 border-b flex items-center gap-2">
                  <Camera className="w-4 h-4 text-green-600" />
                  <h3 className="font-medium text-sm text-green-800">Delivery / Completion Photos</h3>
                </div>
                <div className="p-4">
                  {selectedSO.delivery_photos?.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {selectedSO.delivery_photos.map(photo => (
                        <div key={photo.id} className="relative group">
                          <img src={`${API_URL}${photo.url}`} alt={photo.caption || 'Delivery photo'} className="w-full h-24 object-cover rounded-lg border" />
                          {photo.caption && <p className="text-[10px] text-neutral-500 mt-0.5 truncate">{photo.caption}</p>}
                          <button
                            onClick={async () => {
                              if (!confirm('Delete this photo?')) return;
                              try {
                                await axios.delete(`${API_URL}/admin/sales-orders/${selectedSO.id}/delivery-photos/${photo.id}`, { headers: { Authorization: `Bearer ${token}` } });
                                setSelectedSO(prev => ({ ...prev, delivery_photos: prev.delivery_photos.filter(p => p.id !== photo.id) }));
                                toast.success('Photo deleted');
                              } catch { toast.error('Failed to delete photo'); }
                            }}
                            className="absolute top-1 right-1 p-1 bg-black/50 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-neutral-300 rounded-lg cursor-pointer hover:bg-neutral-50 transition-colors" data-testid="upload-delivery-photo">
                    <Upload className="w-4 h-4 text-neutral-400" />
                    <span className="text-sm text-neutral-500">Upload completion photo</span>
                    <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const formData = new FormData();
                      formData.append('photo', file);
                      formData.append('caption', '');
                      try {
                        const res = await axios.post(`${API_URL}/admin/sales-orders/${selectedSO.id}/delivery-photos`, formData, {
                          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                        });
                        setSelectedSO(prev => ({ ...prev, delivery_photos: [...(prev.delivery_photos || []), res.data] }));
                        toast.success('Photo uploaded!');
                      } catch { toast.error('Upload failed'); }
                      e.target.value = '';
                    }} />
                  </label>
                </div>
              </div>

              {/* Change Request */}
              {selectedSO.change_request && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="font-medium text-red-800 mb-1">Change Requested</h3>
                  <p className="text-red-700">{selectedSO.change_request}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedSO && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowEditModal(false)}>
          <div className="bg-white rounded-lg max-w-lg w-full" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-neutral-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Edit Sales Order {selectedSO.so_number}</h2>
              <button onClick={() => setShowEditModal(false)} className="p-2 hover:bg-neutral-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <Label>Fulfillment Type</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {FULFILLMENT_TYPES.map(type => (
                    <button
                      key={type.id}
                      onClick={() => setEditData({...editData, fulfillment_type: type.id})}
                      className={`p-3 border rounded-lg text-center transition-colors ${
                        editData.fulfillment_type === type.id 
                          ? 'border-amber-500 bg-amber-50' 
                          : 'border-neutral-200 hover:border-neutral-300'
                      }`}
                    >
                      <type.icon className="w-5 h-5 mx-auto mb-1" />
                      <span className="text-sm font-medium">{type.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              {editData.fulfillment_type === 'pickup' && (
                <div>
                  <Label>Pickup Location</Label>
                  <Input
                    value={editData.pickup_location}
                    onChange={(e) => setEditData({...editData, pickup_location: e.target.value})}
                    placeholder="e.g., Brooklyn Showroom"
                    className="mt-1"
                  />
                </div>
              )}

              {editData.fulfillment_type === 'delivery' && (
                <div>
                  <Label>Delivery Notes</Label>
                  <Input
                    value={editData.delivery_notes}
                    onChange={(e) => setEditData({...editData, delivery_notes: e.target.value})}
                    placeholder="Special delivery instructions..."
                    className="mt-1"
                  />
                </div>
              )}

              <div>
                <Label>Notes</Label>
                <textarea
                  value={editData.notes}
                  onChange={(e) => setEditData({...editData, notes: e.target.value})}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-lg mt-1"
                  rows={3}
                  placeholder="Internal notes..."
                />
              </div>

              {/* Installation Scheduling */}
              <div className="border-t pt-4 space-y-3">
                <h4 className="text-sm font-semibold flex items-center gap-1.5 text-amber-700">
                  <CalendarDays className="w-4 h-4" /> Installation Schedule
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Installation Date</Label>
                    <Input
                      type="date"
                      value={editData.installation_date || ''}
                      onChange={(e) => setEditData({...editData, installation_date: e.target.value})}
                      className="mt-1"
                      data-testid="so-install-date"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Assigned Installer</Label>
                    <select
                      value={editData.assigned_installer_id || ''}
                      onChange={(e) => {
                        const inst = installers.find(i => i.id === e.target.value);
                        setEditData({
                          ...editData,
                          assigned_installer_id: e.target.value,
                          assigned_installer_name: inst?.name || ''
                        });
                      }}
                      className="w-full mt-1 rounded-md border border-neutral-300 p-2 text-sm"
                      data-testid="so-installer-select"
                    >
                      <option value="">— None —</option>
                      {installers.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-neutral-200 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
              <Button onClick={updateSO} className="btn-primary">Save Changes</Button>
            </div>
          </div>
        </div>
      )}

      {/* Label Settings Modal */}
      {showLabelSettings && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowLabelSettings(false)}>
          <div className="bg-white rounded-lg max-w-2xl w-full" onClick={e => e.stopPropagation()} data-testid="label-settings-modal">
            <div className="p-6 border-b border-neutral-200">
              <h2 className="text-lg font-semibold flex items-center gap-2"><Printer className="w-5 h-5" /> Label Field Settings</h2>
              <p className="text-sm text-neutral-500 mt-1">Choose which fields appear on shipping labels</p>
            </div>
            <div className="p-6 grid grid-cols-2 gap-6">
              {/* Toggle switches */}
              <div className="space-y-3">
                <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">Field Visibility</p>
                {[
                  { key: 'room_window', label: 'Room / Window' },
                  { key: 'product_type', label: 'Product Type' },
                  { key: 'size', label: 'Size (W x H)' },
                  { key: 'fabric', label: 'Fabric' },
                  { key: 'mount_type', label: 'Mount Type' },
                  { key: 'shipping_address', label: 'Shipping Address' },
                ].map(({ key, label }) => (
                  <label key={key} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-neutral-50 cursor-pointer" data-testid={`label-toggle-${key}`}>
                    <span className="text-sm font-medium text-neutral-700">{label}</span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={labelFields[key]}
                      onClick={() => setLabelFields(prev => ({ ...prev, [key]: !prev[key] }))}
                      className={`relative w-10 h-6 rounded-full transition-colors ${labelFields[key] ? 'bg-green-500' : 'bg-neutral-300'}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${labelFields[key] ? 'translate-x-4' : ''}`} />
                    </button>
                  </label>
                ))}
              </div>

              {/* Live Preview */}
              <div>
                <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">Preview</p>
                <div className="border-2 border-neutral-900 rounded-lg p-0 overflow-hidden" style={{ aspectRatio: '4/3' }} data-testid="label-preview">
                  <div className="bg-neutral-900 px-3 py-2 flex items-center justify-between">
                    <span className="text-white text-xs font-bold tracking-wider" style={{ fontFamily: 'Georgia, serif' }}>APOLLO SHADES</span>
                    <span className="text-white text-[10px]">SO #1042 — 1/3</span>
                  </div>
                  <div className="px-3 py-2 space-y-1">
                    {labelFields.room_window && (
                      <p className="text-sm font-bold text-neutral-800">Living Room — Window 1</p>
                    )}
                    {labelFields.product_type && (
                      <div className="flex gap-1 text-xs"><span className="font-semibold text-neutral-600">Product:</span><span>Roller Shades</span></div>
                    )}
                    {labelFields.size && (
                      <div className="flex gap-1 text-xs"><span className="font-semibold text-neutral-600">Size:</span><span>48" W x 72" H</span></div>
                    )}
                    {labelFields.fabric && (
                      <div className="flex gap-1 text-xs"><span className="font-semibold text-neutral-600">Fabric:</span><span>Ivory Linen 3%</span></div>
                    )}
                    {labelFields.mount_type && (
                      <div className="flex gap-1 text-xs"><span className="font-semibold text-neutral-600">Mount:</span><span>Inside</span></div>
                    )}
                    {labelFields.shipping_address && (
                      <>
                        <div className="border-t border-neutral-200 mt-1.5 pt-1.5">
                          <p className="text-[10px] font-bold text-neutral-600">SHIP TO:</p>
                          <p className="text-xs text-neutral-700">John Smith</p>
                          <p className="text-xs text-neutral-500">123 Main St, Brooklyn, NY 11205</p>
                        </div>
                      </>
                    )}
                    {!Object.values(labelFields).some(v => v) && (
                      <p className="text-xs text-neutral-400 italic py-4 text-center">No fields selected</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-neutral-200 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowLabelSettings(false)}>Cancel</Button>
              <Button onClick={saveLabelSettings} disabled={savingLabels} className="bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="save-label-settings-btn">
                {savingLabels ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesOrdersTab;
